import { themeAtom } from "../../../shared/atoms";
import {
  AdminUwhcBreakdownItem,
  AdminUwhcCoverageStat,
  AdminUwhcLocationPin,
  AdminUwhcMetric,
  AdminUwhcMonitoringStats,
  AdminUwhcProviderRow,
  AdminUwhcQualitySignal,
} from "../../models/admin-uwhc.models";
import { ADMIN_PATHS } from "../../models/admin.models";
import { getAdminUwhcErrorMessage, getAdminUwhcProviders } from "../../services/admin-uwhc.service";
import { adminStyles } from "../../styles/admin.styles";
import { buildAdminUwhcMonitoringStats, toAdminUwhcGridRow } from "../../utils/admin-uwhc.utils";
import { ArrowPathIcon, ChartBarIcon, ExclamationTriangleIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { twMerge } from "tailwind-merge";

type LoadingState = "loading" | "ready" | "error";

const metricToneClasses: Record<AdminUwhcMetric["tone"], string> = {
  accent: "bg-[#CCE4E6] text-[#003641]",
  success: "bg-[#EFFAF6] text-[#0F5F54]",
  warning: "bg-[#FFF8E1] text-[#946200]",
  danger: "bg-[#FFEAED] text-[#BF0002]",
  neutral: "bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-300",
};

const WORLD_MAP_VIEWBOX = "-30 61 2754 1398";
const WORLD_MAP_X = -30;
const WORLD_MAP_Y = 61;
const WORLD_MAP_WIDTH = 2754;
const WORLD_MAP_HEIGHT = 1398;
const MAP_UNITS_PER_PIXEL = WORLD_MAP_HEIGHT / 430;

/**
 * Monitoring dashboard derived directly from active UWHC provider rows.
 */
const AdminUwhcMonitoringPanel = () => {
  const theme = useAtomValue(themeAtom);
  const [rows, setRows] = useState<AdminUwhcProviderRow[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadProviders = useCallback(async () => {
    setLoadingState("loading");
    setErrorMessage(null);

    try {
      const response = await getAdminUwhcProviders();
      setRows(response.rows.map((row) => toAdminUwhcGridRow(row)));
      setLoadingState("ready");
    } catch (error) {
      setErrorMessage(getAdminUwhcErrorMessage(error));
      setLoadingState("error");
    }
  }, []);

  useEffect(() => {
    void loadProviders();
  }, [loadProviders]);

  const stats = useMemo(() => buildAdminUwhcMonitoringStats(rows), [rows]);

  return (
    <main
      data-test="admin-uwhc-monitoring-page"
      className={twMerge("flex min-h-0 flex-1 flex-col overflow-hidden", theme.home.tailwind.textDefault)}
    >
      <AdminUwhcMonitoringTopBar loadingState={loadingState} updatedAt={stats.updatedAt} onRefresh={loadProviders} />

      <div
        className={twMerge(
          "min-h-0 flex-1 overflow-y-auto px-5 py-4",
          "[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar]:w-2.5",
          "[&::-webkit-scrollbar-thumb]:bg-[#B7C9C4] [&::-webkit-scrollbar-track]:bg-[#F3F4F4]",
        )}
      >
        {errorMessage && <ErrorPanel message={errorMessage} />}
        {loadingState === "loading" && rows.length === 0 ? <LoadingPanel /> : <Dashboard stats={stats} />}
      </div>
    </main>
  );
};

// === PAGE PARTS ===

const AdminUwhcMonitoringTopBar = ({
  loadingState,
  updatedAt,
  onRefresh,
}: {
  loadingState: LoadingState;
  updatedAt: string;
  onRefresh: () => void;
}) => {
  const theme = useAtomValue(themeAtom);

  return (
    <header
      data-test="admin-uwhc-monitoring-top-bar"
      className="relative z-50 flex h-11 flex-none items-center border-b border-gray-200 bg-white px-3 dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden pr-3">
        <h1 className={twMerge("min-w-0 truncate text-sm font-semibold leading-5", theme.home.tailwind.textDefault)}>
          UWHC Provider Signals
        </h1>
        <span className="hidden flex-none border border-[#D7E2DE] px-2 py-1 text-[11px] font-semibold text-[#4A5655] md:inline-flex">
          Updated {formatTime(updatedAt)}
        </span>
      </div>
      <nav className="flex flex-none items-center gap-1 text-xs font-semibold" aria-label="UWHC admin views">
        <TopBarLink to={ADMIN_PATHS.uwhc} dataTest="admin-uwhc-monitoring-topbar-editor-link">
          Editor
        </TopBarLink>
        <TopBarLink to={ADMIN_PATHS.monitoringUwhc} dataTest="admin-uwhc-monitoring-topbar-monitoring-link">
          Monitor
        </TopBarLink>
      </nav>
      <button
        type="button"
        data-test="admin-uwhc-monitoring-refresh"
        disabled={loadingState === "loading"}
        className="ml-3 inline-flex h-8 min-w-8 flex-none items-center justify-center gap-1.5 bg-green px-2 text-xs font-semibold text-white transition-colors hover:bg-[#166b60] disabled:cursor-not-allowed disabled:opacity-50 2xl:px-3"
        onClick={onRefresh}
      >
        <ArrowPathIcon className={twMerge("h-4 w-4", loadingState === "loading" && "animate-spin")} />
        <span className="hidden xl:inline">Refresh</span>
      </button>
    </header>
  );
};

const TopBarLink = ({ to, dataTest, children }: { to: string; dataTest: string; children: string }) => (
  <NavLink
    to={to}
    data-test={dataTest}
    className={({ isActive }) =>
      twMerge(
        "inline-flex h-8 items-center border px-3 transition-colors",
        isActive ? "border-green bg-[#E9F4F3] text-green" : "border-gray-200 hover:border-green dark:border-gray-700",
      )
    }
  >
    {children}
  </NavLink>
);

const Dashboard = ({ stats }: { stats: AdminUwhcMonitoringStats }) => (
  <div className="space-y-5" data-test="admin-uwhc-monitoring-dashboard">
    <MetricGrid metrics={stats.metrics} />

    <div className="grid gap-5 2xl:grid-cols-[minmax(0,1.45fr)_minmax(360px,0.55fr)]">
      <LocationMap pins={stats.locationPins} unmatchedLocations={stats.unmatchedLocations} />
      <QualityPanel signals={stats.qualitySignals} contactCoverage={stats.contactCoverage} />
    </div>

    <div className="grid gap-5 xl:grid-cols-2">
      <ServiceCoveragePanel items={stats.topServices} />
      <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
        <BreakdownPanel title="Top Countries" items={stats.topCountries} dataTest="admin-uwhc-monitoring-countries" />
        <BreakdownPanel title="Top Regions" items={stats.topRegions} dataTest="admin-uwhc-monitoring-regions" />
      </div>
    </div>
  </div>
);

const MetricGrid = ({ metrics }: { metrics: AdminUwhcMetric[] }) => (
  <div className="grid grid-cols-2 gap-3 lg:grid-cols-5" data-test="admin-uwhc-monitoring-kpis">
    {metrics.map((metric) => (
      <MetricCard key={metric.key} metric={metric} />
    ))}
  </div>
);

const MetricCard = ({ metric }: { metric: AdminUwhcMetric }) => (
  <section
    className={twMerge(adminStyles.card.indicator, metricToneClasses[metric.tone])}
    data-test={`admin-uwhc-monitoring-kpi-${metric.key}`}
  >
    <p className="truncate text-[10px] font-bold uppercase opacity-70">{metric.label}</p>
    <p className="mt-3 truncate text-5xl font-bold leading-none">{metric.value}</p>
    {metric.caption && <p className="mt-1 truncate text-xs opacity-75">{metric.caption}</p>}
  </section>
);

const LocationMap = ({ pins, unmatchedLocations }: { pins: AdminUwhcLocationPin[]; unmatchedLocations: number }) => (
  <section className={twMerge(adminStyles.card.surface, "overflow-hidden")} data-test="admin-uwhc-monitoring-map">
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
      <div>
        <h2 className="text-sm font-semibold">Provider location map</h2>
        <p className="mt-1 text-xs opacity-70">{pins.length.toLocaleString()} matched location groups</p>
      </div>
      <div className="flex flex-wrap gap-2 text-[11px] font-semibold">
        <LegendDot className="bg-green" label="Place" />
        <LegendDot className="bg-[#2F84A5]" label="Country" />
        {unmatchedLocations > 0 && <span className="text-[#8F0002]">{unmatchedLocations} not mapped</span>}
      </div>
    </div>

    <div className="relative h-[430px] overflow-hidden bg-[#EAF4F2]">
      <WorldSketch pins={pins} />
    </div>
  </section>
);

const WorldSketch = ({ pins }: { pins: AdminUwhcLocationPin[] }) => (
  <>
    <svg viewBox="0 0 1000 430" className="absolute inset-0 h-full w-full" aria-hidden="true">
      <rect width="1000" height="430" fill="#EAF4F2" />
      {[111, 222, 333, 444, 556, 667, 778, 889].map((x) => (
        <line key={`x-${x}`} x1={x} x2={x} y1="18" y2="412" stroke="#C8DCD7" strokeWidth="1" opacity="0.42" />
      ))}
      {[72, 143, 215, 287, 358].map((y) => (
        <line key={`y-${y}`} x1="22" x2="978" y1={y} y2={y} stroke="#C8DCD7" strokeWidth="1" opacity="0.45" />
      ))}
    </svg>
    <svg viewBox={WORLD_MAP_VIEWBOX} className="absolute inset-0 h-full w-full" aria-hidden="true">
      <image
        href={`${process.env.PUBLIC_URL}/assets/uwhc-world-map.svg`}
        x={WORLD_MAP_X}
        y={WORLD_MAP_Y}
        width={WORLD_MAP_WIDTH}
        height={WORLD_MAP_HEIGHT}
        preserveAspectRatio="none"
        opacity="0.95"
      />
      {pins.map((pin) => (
        <MapPin key={pin.key} pin={pin} />
      ))}
    </svg>
  </>
);

const MapPin = ({ pin }: { pin: AdminUwhcLocationPin }) => {
  const radius = (Math.min(28, 10 + pin.count * 2) / 2) * MAP_UNITS_PER_PIXEL;
  const fontSize = 10 * MAP_UNITS_PER_PIXEL;
  const color =
    pin.matchLevel === "place" ? "#1F8A7A" : pin.matchLevel === "country" ? "#2F84A5" : "#BF0002";

  return (
    <g
      transform={`translate(${pin.x} ${pin.y})`}
      data-test={`admin-uwhc-monitoring-map-pin-${pin.key}`}
      className="drop-shadow"
    >
      <title>{`${pin.locationLabel}: ${pin.count} provider${pin.count === 1 ? "" : "s"}`}</title>
      <circle r={radius} fill={color} stroke="#FFFFFF" strokeWidth={2 * MAP_UNITS_PER_PIXEL} />
      {pin.count > 1 && (
        <text
          fill="#FFFFFF"
          fontSize={fontSize}
          fontWeight="700"
          textAnchor="middle"
          dominantBaseline="central"
          pointerEvents="none"
        >
          {pin.count}
        </text>
      )}
    </g>
  );
};

const LegendDot = ({ className, label }: { className: string; label: string }) => (
  <span className="inline-flex items-center gap-1">
    <span className={twMerge("h-2.5 w-2.5 rounded-full", className)} />
    {label}
  </span>
);

const QualityPanel = ({
  signals,
  contactCoverage,
}: {
  signals: AdminUwhcQualitySignal[];
  contactCoverage: AdminUwhcCoverageStat[];
}) => (
  <aside className="grid gap-5" data-test="admin-uwhc-monitoring-quality">
    <section className={twMerge(adminStyles.card.surface, "p-4")}>
      <div className="mb-4 flex items-center gap-2">
        <ExclamationTriangleIcon className="h-5 w-5 text-green" />
        <h2 className="text-sm font-semibold">Data quality signals</h2>
      </div>
      <div className="grid gap-3">
        {signals.map((signal) => (
          <SignalCard key={signal.key} signal={signal} />
        ))}
      </div>
      <p
        className="mt-3 text-[11px] leading-4 text-gray-500 dark:text-gray-400"
        data-test="admin-uwhc-monitoring-no-services-explanation"
      >
        * Counts provider records where all tracked service and service-related fields are blank, set to No, or
        otherwise not marked as available.
      </p>
    </section>

    <section className={twMerge(adminStyles.card.surface, "p-4")}>
      <div className="mb-4 flex items-center gap-2">
        <MapPinIcon className="h-5 w-5 text-green" />
        <h2 className="text-sm font-semibold">Contact and context coverage</h2>
      </div>
      <CoverageList items={contactCoverage} />
    </section>
  </aside>
);

const SignalCard = ({ signal }: { signal: AdminUwhcQualitySignal }) => (
  <div
    className={twMerge("flex items-center justify-between gap-3 px-3 py-3 shadow-sm", metricToneClasses[signal.tone])}
  >
    <span className="min-w-0 truncate text-xs font-semibold">{signal.label}</span>
    <span className="text-3xl font-bold leading-8">{signal.value.toLocaleString()}</span>
  </div>
);

const ServiceCoveragePanel = ({ items }: { items: AdminUwhcCoverageStat[] }) => (
  <section className={twMerge(adminStyles.card.surface, "p-4")} data-test="admin-uwhc-monitoring-service-coverage">
    <div className="mb-4 flex items-center gap-2">
      <ChartBarIcon className="h-5 w-5 text-green" />
      <h2 className="text-sm font-semibold">Strongest service coverage</h2>
    </div>
    <CoverageList items={items} />
  </section>
);

const CoverageList = ({ items }: { items: AdminUwhcCoverageStat[] }) => (
  <div className="space-y-3">
    {items.map((item) => (
      <div key={item.key} className="grid grid-cols-[minmax(120px,220px)_1fr_auto] items-center gap-3 text-xs">
        <span className="truncate font-semibold" title={item.label}>
          {item.label}
        </span>
        <div className="h-3 min-w-0 bg-[#E9F4F3] dark:bg-gray-700">
          <div className="h-full bg-green" style={{ width: `${Math.max(2, Math.round(item.rate * 100))}%` }} />
        </div>
        <span className="w-16 text-right font-semibold">{item.count.toLocaleString()}</span>
      </div>
    ))}
  </div>
);

const BreakdownPanel = ({
  title,
  items,
  dataTest,
}: {
  title: string;
  items: AdminUwhcBreakdownItem[];
  dataTest: string;
}) => {
  const maxValue = Math.max(...items.map((item) => item.value), 1);

  return (
    <section className={twMerge(adminStyles.card.surface, "p-4")} data-test={dataTest}>
      <h2 className="mb-4 text-sm font-semibold">{title}</h2>
      {items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.label} className="grid grid-cols-[minmax(90px,150px)_1fr_auto] items-center gap-3 text-xs">
              <span className="truncate font-semibold" title={item.label}>
                {item.label}
              </span>
              <div className="h-3 min-w-0 bg-[#E9F4F3] dark:bg-gray-700">
                <div
                  className="h-full bg-[#2F84A5]"
                  style={{ width: `${Math.round((item.value / maxValue) * 100)}%` }}
                />
              </div>
              <span className="w-12 text-right font-semibold">{item.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="border-greyscale-20 border border-dashed px-4 py-5 text-sm opacity-70 dark:border-gray-700">
          No values captured.
        </p>
      )}
    </section>
  );
};

const LoadingPanel = () => (
  <div className={adminStyles.card.loading}>
    <ArrowPathIcon className="mr-2 h-5 w-5 animate-spin text-green" />
    Loading UWHC provider signals
  </div>
);

const ErrorPanel = ({ message }: { message: string }) => (
  <div
    role="alert"
    data-test="admin-uwhc-monitoring-error"
    className="mb-4 border border-[#F59692] bg-[#FFEAED] px-4 py-3 text-sm text-[#BF0002]"
  >
    {message}
  </div>
);

function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default AdminUwhcMonitoringPanel;
